import React from 'react';
import { Chip } from '@mui/material';
import { IPD_COLORS, INV_CHART, INV_RADIUS, INV_TYPE, INV_ALPHA } from '../theme';
import { ISSUE_STATUS_LABELS, PRESCRIPTION_STATUS_LABELS } from '../constants';

// Pending / in-progress → C.success (#198754), same as IPD Billing discharge "ok" states
const SUCCESS_CHIP = { bg: IPD_COLORS.success.text, text: IPD_COLORS.white };
const WARNING_DARK_CHIP = { bg: IPD_COLORS.warning.text, text: '#fff' };

const STATUS_COLORS = {
  DRAFT: { bg: IPD_COLORS.textPrimary, text: '#fff' },
  SUBMITTED: SUCCESS_CHIP,
  PENDING_APPROVAL: SUCCESS_CHIP,
  PENDING: SUCCESS_CHIP,
  // GRN ramp: DRAFT navy → RECEIVED blue → QC_PENDING amber → QC_COMPLETED slate → POSTED green.
  QC_PENDING: WARNING_DARK_CHIP,
  APPROVED: { bg: IPD_COLORS.primary, text: '#fff' },
  NOT_REQUIRED: { bg: IPD_COLORS.textSecondary, text: '#fff' },
  RESERVED: { bg: IPD_COLORS.info.text, text: '#fff' },
  AWAITING_AUTHORIZATION: WARNING_DARK_CHIP,
  PARTIALLY_DISPENSED: { bg: IPD_COLORS.info.text, text: '#fff' },
  ISSUED: SUCCESS_CHIP,
  MATCHED: SUCCESS_CHIP,
  VARIANCE: WARNING_DARK_CHIP,
  REVERSED: { bg: IPD_COLORS.error.text, text: '#fff' },
  DISPENSED: SUCCESS_CHIP,
  CONSUMED: SUCCESS_CHIP,
  CONFIRMED: SUCCESS_CHIP,
  ACKNOWLEDGED: SUCCESS_CHIP,
  PARTIALLY_ISSUED: WARNING_DARK_CHIP,
  PARTIALLY_RETURNED: WARNING_DARK_CHIP,
  AUTHORIZED: SUCCESS_CHIP,
  PENDING_INSURANCE: WARNING_DARK_CHIP,
  RETURNED: SUCCESS_CHIP,
  COMPLETED: SUCCESS_CHIP,
  CLOSED: SUCCESS_CHIP,
  CONSOLIDATED: { bg: IPD_COLORS.primary, text: '#fff' },
  PO_CREATED: SUCCESS_CHIP,
  POSTED: SUCCESS_CHIP,
  POSTED_TO_STOCK: SUCCESS_CHIP,
  // primaryLight, not primary — IPD_COLORS.primary is the same hex as textPrimary,
  // which would make a QC-complete GRN indistinguishable from a DRAFT one.
  QC_COMPLETED: { bg: IPD_COLORS.primaryLight, text: '#fff' },
  REJECTED: { bg: IPD_COLORS.error.text, text: '#fff' },
  CANCELLED: { bg: IPD_COLORS.error.text, text: '#fff' },
  FAILED: { bg: IPD_COLORS.error.text, text: '#fff' },
  OPEN: { bg: IPD_COLORS.primaryLight, text: '#fff' },
  PUBLISHED: SUCCESS_CHIP,
  EVALUATED: { bg: IPD_COLORS.primary, text: '#fff' },
  AWARDED: { bg: IPD_COLORS.primaryDark, text: '#fff' },
  IN_PROGRESS: { bg: IPD_COLORS.info.text, text: '#fff' },
  ON_HOLD: WARNING_DARK_CHIP,
  SENT_TO_VENDOR: { bg: IPD_COLORS.info.text, text: '#fff' },
  RECEIVED: { bg: IPD_COLORS.info.text, text: '#fff' },
  PARTIALLY_RECEIVED: WARNING_DARK_CHIP,
  FULLY_RECEIVED: SUCCESS_CHIP,
  INVOICE_GENERATED: { bg: IPD_COLORS.info.text, text: '#fff' },
  INVOICED: { bg: IPD_COLORS.info.text, text: '#fff' },
  PAYMENT_COMPLETED: { bg: IPD_COLORS.success.text, text: '#fff' },
  PAID: { bg: IPD_COLORS.success.text, text: '#fff' },
  ACTIVE: SUCCESS_CHIP,
  SENT: SUCCESS_CHIP,
  INACTIVE: { bg: IPD_COLORS.error.text, text: '#fff' },
  SUSPENDED: WARNING_DARK_CHIP,
  BLACKLISTED: { bg: IPD_COLORS.error.text, text: '#fff' },
};

/** Stock-issue transaction: ISSUED means awaiting receiver acknowledgement (not complete). */
const ISSUE_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  ISSUED: WARNING_DARK_CHIP,
  ACKNOWLEDGED: { bg: INV_CHART.purple, text: '#fff' },
};

/**
 * Requisition lifecycle badge — scoped override so DRAFT/SUBMITTED/APPROVED can match the
 * grey/blue/green spec here without recoloring those statuses everywhere else (POs, GRNs, Vendors).
 */
const REQUISITION_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  DRAFT: { bg: IPD_COLORS.textSecondary, text: '#fff' },
  SUBMITTED: { bg: IPD_COLORS.info.text, text: '#fff' },
  APPROVED: { bg: IPD_COLORS.success.text, text: '#fff' },
};

/** Pharmacy bill lifecycle badge — grey/green/amber/red per the billing spec. */
const BILL_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  DRAFT: { bg: IPD_COLORS.textSecondary, text: '#fff' },
  PAID: { bg: IPD_COLORS.success.text, text: '#fff' },
  PARTIALLY_PAID: WARNING_DARK_CHIP,
  CANCELLED: { bg: IPD_COLORS.error.text, text: '#fff' },
};

/** Pharmacy payment/refund badge — refund statuses included for the Phase 2 refunds UI. */
const PAYMENT_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  SUCCESS: { bg: IPD_COLORS.success.text, text: '#fff' },
  PENDING: WARNING_DARK_CHIP,
  APPROVED: { bg: IPD_COLORS.primary, text: '#fff' },
  REJECTED: { bg: IPD_COLORS.error.text, text: '#fff' },
  SETTLED: { bg: IPD_COLORS.success.text, text: '#fff' },
};

/** Ward-return inbox: SUBMITTED = awaiting pharmacy (amber), not generic green. */
const WARD_RETURN_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  DRAFT: { bg: IPD_COLORS.textSecondary, text: '#fff' },
  SUBMITTED: WARNING_DARK_CHIP,
  RETURNED: { bg: IPD_COLORS.success.text, text: '#fff' },
  PARTIALLY_RETURNED: { bg: IPD_COLORS.info.text, text: '#fff' },
  REJECTED: { bg: IPD_COLORS.error.text, text: '#fff' },
  CANCELLED: { bg: IPD_COLORS.textSecondary, text: '#fff' },
};

/**
 * QC replacement lifecycle — PENDING is outstanding work, so it must stay amber rather than
 * take the base map's green, which would also make it indistinguishable from POSTED/CLOSED.
 */
const REPLACEMENT_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  PENDING: WARNING_DARK_CHIP,
  CREATED: { bg: IPD_COLORS.info.text, text: '#fff' },
  POSTED: SUCCESS_CHIP,
};

/**
 * IPD medication order book (CPOE): Sent → Acknowledged → Dispensed → Received,
 * with Cancel outside the chain. Scoped so the base map keeps SENT/ACTIVE green for
 * vendors and item masters, which read those same codes with a different meaning.
 *
 * CANCEL is the load-bearing entry — the base map only has CANCELLED, so a cancelled
 * order fell through to the default navy chip and read like a DRAFT.
 */
const PRESCRIPTION_DOMAIN_COLORS = {
  ...STATUS_COLORS,
  ACTIVE: WARNING_DARK_CHIP,
  SENT: WARNING_DARK_CHIP,
  ACKNOWLEDGED: { bg: IPD_COLORS.info.text, text: '#fff' },
  // Same blue family as Acknowledged: a part-filled order is still open work,
  // not a warning and not a completion.
  PARTIAL: { bg: IPD_COLORS.info.text, text: '#fff' },
  // primaryLight, not success — Dispensed and Received are both terminal, and a
  // ward that has not confirmed receipt yet must stay visually distinct.
  DISPENSED: { bg: IPD_COLORS.primaryLight, text: '#fff' },
  RECEIVED: SUCCESS_CHIP,
  CANCEL: { bg: IPD_COLORS.error.text, text: '#fff' },
  // Grey, not red — closed without supply is an outcome, not an error; red stays
  // reserved for Cancel (clinical withdrawal).
  SHORT_CLOSED: { bg: IPD_COLORS.textSecondary, text: '#fff' },
  // Derived from SHORT_CLOSED when accepted=0; same grey for consistency.
  NOT_SUPPLIED: { bg: IPD_COLORS.textSecondary, text: '#fff' },
};

const DOMAIN_PALETTES = {
  issue: ISSUE_DOMAIN_COLORS,
  prescription: PRESCRIPTION_DOMAIN_COLORS,
  requisition: REQUISITION_DOMAIN_COLORS,
  bill: BILL_DOMAIN_COLORS,
  payment: PAYMENT_DOMAIN_COLORS,
  wardReturn: WARD_RETURN_DOMAIN_COLORS,
  replacement: REPLACEMENT_DOMAIN_COLORS,
};

const DOMAIN_LABELS = {
  issue: ISSUE_STATUS_LABELS,
  prescription: PRESCRIPTION_STATUS_LABELS,
};

/** The only two sanctioned badge heights. `xs` is for in-cell markers (CD, Primary, Expired). */
const CHIP_SIZES = {
  sm: { height: 24, fontSize: INV_TYPE.chip.fontSize },
  xs: { height: 18, fontSize: 10 },
};

/**
 * Canonical badge geometry for the whole module. Exported so the domain badges
 * (BatchChip, ModuleBadge, ActiveStatusBadge, StoreTypeChip, ControlledDrugBadge)
 * share one box instead of each re-deriving its own height/radius/type scale.
 *
 * @param color   base hex — the fill in `solid`, the ink in `soft`/`outline`
 * @param onColor ink for `solid` (defaults to white)
 */
export function invChipSx({ color, onColor = '#fff', variant = 'solid', size = 'sm', minWidth } = {}) {
  const dim = CHIP_SIZES[size] || CHIP_SIZES.sm;
  const skin = {
    solid: { backgroundColor: color, color: onColor, border: 'none' },
    soft: {
      backgroundColor: `${color}${INV_ALPHA.tint}`,
      color,
      border: `1px solid ${color}${INV_ALPHA.border}`,
    },
    outline: { backgroundColor: 'transparent', color, border: `1px solid ${color}${INV_ALPHA.border}` },
  }[variant] || {};

  return {
    ...skin,
    height: dim.height,
    fontSize: dim.fontSize,
    fontWeight: INV_TYPE.chip.fontWeight,
    letterSpacing: INV_TYPE.chip.letterSpacing,
    borderRadius: `${INV_RADIUS.chip}px`,
    justifyContent: 'center',
    // Callers that pass minWidth want a fixed equal-width column of badges
    // (Returns, discards, etc.) — set width too so short labels don't shrink.
    ...(minWidth != null ? { minWidth, width: minWidth, flexShrink: 0 } : null),
    '& .MuiChip-label': { px: 1, width: '100%', textAlign: 'center' },
  };
}

export default function StatusChip({ status, label, domain, minWidth, variant = 'solid', size = 'sm' }) {
  if (!status) return null;
  const palette = DOMAIN_PALETTES[domain] || STATUS_COLORS;
  const c = palette[status] || { bg: IPD_COLORS.textPrimary, text: '#fff' };
  const display =
    label
    || DOMAIN_LABELS[domain]?.[status]
    || status.replace(/_/g, ' ');
  return (
    <Chip
      label={display}
      size="small"
      sx={invChipSx({ color: c.bg, onColor: c.text, variant, size, minWidth })}
    />
  );
}
